import { Box, Typography } from '@mui/material';
import { tokens } from '../../theme';
import type { AiToolChipProps, AiToolRowProps } from './types';

/**
 * The AI tooling a phase, process or method names — one blue pill per tool,
 * behind an `AI TOOL:` label.
 *
 * **Blue at every level, never the stage colour.** Everything else on these
 * pages is tinted by lifecycle stage; these are not, because the tool is the
 * same tool wherever it appears and a Copilot chip that changed colour between
 * two pages would read as two different things.
 *
 * The label is the design's own string, singular even with four chips beside
 * it. Left as it is drawn rather than corrected to "AI TOOLS" — it reads as a
 * field name, and changing a label the designs set is the design owner's call.
 */

/** One tool. Not a link: nothing in the catalog is keyed by tool yet. */
export const AiToolChip = ({ label }: AiToolChipProps) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      backgroundColor: 'accent.blueSurface',
      borderRadius: `${String(tokens.radius.full)}px`,
      paddingInline: 3,
      paddingBlock: '5px',
    }}
  >
    <Typography
      component="span"
      sx={{
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1.4,
        color: 'text.link',
        // A tool name is short and fixed — this is the case `nowrap` is
        // genuinely for, unlike the Confluence prose elsewhere on the page.
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
  </Box>
);

export const AiToolRow = ({ tools }: AiToolRowProps) => {
  if (tools === undefined || tools.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        // Wraps rather than scrolls: a method can name five tools and the row
        // sits inside the page's own width on a phone.
        flexWrap: 'wrap',
        gap: '6px',
      }}
    >
      <Typography
        variant="caption"
        component="span"
        sx={{ fontWeight: 700, color: 'text.secondary', marginRight: '6px' }}
      >
        AI TOOL:
      </Typography>

      {tools.map((tool) => (
        <AiToolChip key={tool} label={tool} />
      ))}
    </Box>
  );
};
